import { PUBLIC_ORIGIN } from "../lib/domain";
import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { API } from "../lib/api";
import { toast } from "sonner";
import {
  CalendarDays, Plus, Trash2, MapPin, ExternalLink, Hotel, FileText, Users,
  GraduationCap, Copy, Sparkles, Bell, X, Star, Clock, ChevronRight, Loader2, Pencil,
  Download, Image as ImageIcon, Activity, Mail, Send, Flame,
} from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";
import { DefisKaizenPanel } from "./DefisKaizenPanel";
import PosterStudio from "../components/PosterStudio";
import { SendRegLinkDialog } from "../components/events/SendRegLinkDialog";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const EVENT_TYPES = [
  { v: "competition", l: "Compétition" }, { v: "seminar", l: "Séminaire" },
  { v: "workshop", l: "Stage / Atelier" }, { v: "camp", l: "Camp" },
  { v: "gala", l: "Gala" }, { v: "demonstration", l: "Démonstration" },
  { v: "special", l: "Activité spéciale" }, { v: "instructor", l: "Événement instructeur" },
];
const TYPE_LABEL = (v) => EVENT_TYPES.find((t) => t.v === v)?.l || v;

const ORIGIN = API.replace(/\/api$/, "");
const mediaUrl = (u) => (!u ? "" : (u.startsWith("http") ? u : `${ORIGIN}${u}`));
const ytEmbed = (url) => {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : "";
};

const ROLES = [
  { v: "participant", l: "Compétiteur" }, { v: "eleve", l: "Élève" }, { v: "coach", l: "Coach" },
  { v: "juge", l: "Juge / Arbitre" }, { v: "instructeur", l: "Instructeur" }, { v: "organisateur", l: "Organisateur" },
];
const ROLE_LABEL = (v) => ROLES.find((r) => r.v === v)?.l || v;

const PART_STATUS = {
  interesse: { l: "Intéressé", c: "bg-zinc-700 text-zinc-200" },
  en_attente: { l: "En attente", c: "bg-blue-500/20 text-blue-300" },
  relance: { l: "À relancer", c: "bg-amber-500/20 text-amber-300" },
  inscrit: { l: "Inscrit", c: "bg-cyan-500/20 text-cyan-300" },
  paye: { l: "Payé", c: "bg-emerald-500/20 text-emerald-300" },
  hotel_reserve: { l: "Hôtel réservé", c: "bg-violet-500/20 text-violet-300" },
  transport_organise: { l: "Transport OK", c: "bg-teal-500/20 text-teal-300" },
  annule: { l: "Annulé", c: "bg-red-500/20 text-red-300" },
};

const STATUS_BADGE = {
  draft: { l: "Brouillon", c: "bg-zinc-700 text-zinc-300" },
  published: { l: "Publié", c: "bg-emerald-500/20 text-emerald-300" },
  closed: { l: "Fermé", c: "bg-amber-500/20 text-amber-300" },
  archived: { l: "Archivé", c: "bg-zinc-800 text-zinc-500" },
  cancelled: { l: "Annulé", c: "bg-red-500/20 text-red-300" },
};

const EMPTY_EVENT = {
  event_title: "", event_type: "competition", discipline: "", short_description: "", long_description: "",
  start_date: "", end_date: "", city: "", venue_name: "", venue_address: "",
  organizer_name: "", organizer_contact: "", official_registration_url: "", hotel_url: "",
  schedule_url: "", highlights_url: "", documents_url: "", poster_image: "", instructor_role: "participant",
  instructor_teaching: false, equipment_required: "", documents_to_bring: "",
  dojo_deadline: "", registration_deadline: "", hotel_deadline: "", estimated_cost: "",
  cost_inscription: 0, cost_hotel: 0, cost_transport: 0,
  replacement_instructor: "", internal_notes: "", public_notes: "", status: "published",
  featured_event: false, is_full: false, tags: [],
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
};
const fmtRange = (a, b) => (b && b !== a ? `${fmtDate(a)} → ${fmtDate(b)}` : fmtDate(a));
const isPast = (e) => { const end = e.end_date || e.start_date; return end && new Date(end + "T23:59:59") < new Date(); };

export default function EventsModule() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming"); // upcoming | teaching | participants | archives
  const [filterType, setFilterType] = useState("");
  const [filterDisc, setFilterDisc] = useState("");
  const [editModal, setEditModal] = useState(null); // null | {form, id?}
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dojo-events");
      setEvents(data.events || []);
    } catch { toast.error("Erreur chargement événements"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const disciplines = useMemo(
    () => [...new Set(events.map((e) => e.discipline).filter(Boolean))], [events]);

  const filtered = useMemo(() => {
    let list = events;
    if (tab === "teaching") list = list.filter((e) => e.instructor_teaching);
    else if (tab === "archives") list = list.filter((e) => e.status === "archived" || isPast(e));
    else if (tab === "upcoming") list = list.filter((e) => e.status !== "archived" && !isPast(e));
    if (filterType) list = list.filter((e) => e.event_type === filterType);
    if (filterDisc) list = list.filter((e) => e.discipline === filterDisc);
    return [...list].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  }, [events, tab, filterType, filterDisc]);

  const seed = async () => {
    try {
      const { data } = await api.post("/dojo-events/seed");
      if (data.count) toast.success(`${data.count} événement(s) ajouté(s) : ${data.created.join(", ")}`);
      else toast.info("Événements initiaux déjà présents.");
      load();
    } catch { toast.error("Erreur seed"); }
  };

  const runReminders = async () => {
    try {
      const { data } = await api.post("/dojo-events/reminders/run?dry_run=true");
      if (!data.count) { toast.info("Aucun rappel de date limite à envoyer pour le moment."); return; }
      if (!window.confirm(`⏳ Envoyer ${data.count} rappel(s) de date limite (inscription/hôtel) aux participants ?`)) return;
      const res = await api.post("/dojo-events/reminders/run?dry_run=false");
      toast.success(`✅ ${res.data.sent}/${res.data.count} rappel(s) envoyé(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur rappels"); }
  };

  const runDigest = async () => {
    try {
      const { data } = await api.post("/dojo-events/digest/run?dry_run=true");
      if (!data.pending) { toast.info("Aucun participant à relancer pour le moment."); return; }
      if (!window.confirm(`📧 Envoyer au Kru le résumé de ${data.pending} participant(s) à relancer (${data.events} événement(s)) ?`)) return;
      const res = await api.post("/dojo-events/digest/run?dry_run=false");
      toast.success(res.data.sent ? `✅ Résumé envoyé (${res.data.pending} à relancer)` : `Non envoyé : ${res.data.reason || ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur relances"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement cet événement et ses participations ?")) return;
    try { await api.delete(`/dojo-events/${id}`); toast.success("Supprimé"); if (detailId === id) setDetailId(null); load(); }
    catch { toast.error("Erreur"); }
  };

  const duplicate = async (id) => {
    try { await api.post(`/dojo-events/${id}/duplicate`); toast.success("Événement dupliqué (brouillon)"); load(); }
    catch { toast.error("Erreur"); }
  };

  const cancelEvent = async (id, title) => {
    if (!window.confirm(`Annuler l'événement « ${title || ""} » et STOPPER toutes ses annonces/campagnes automatiques ? L'événement reste dans l'historique (statut « Annulé »).`)) return;
    try {
      const { data } = await api.post(`/dojo-events/${id}/cancel`);
      const s = data.stopped || {};
      const parts = [];
      if (s.event_propositions) parts.push(`${s.event_propositions} proposition(s)`);
      if (s.agent_tasks) parts.push(`${s.agent_tasks} campagne(s) IA`);
      if (s.marketing_publications) parts.push(`${s.marketing_publications} publication(s)`);
      if (s.marketing_boost_posts) parts.push(`${s.marketing_boost_posts} post(s)`);
      toast.success(parts.length ? `Annulé · stoppé : ${parts.join(", ")}` : "Événement annulé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="events-page">
      <PageHeader
        title="Événements & Compétitions"
        icon={CalendarDays}
        breadcrumbs={[{ label: "Compétition & événements" }, { label: "Événements" }]}
        subtitle="Compétitions · séminaires · stages · galas — suivi des inscriptions dojo"
        actions={<>
          <button data-testid="event-add-btn" onClick={() => setEditModal({ form: { ...EMPTY_EVENT } })}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-black font-semibold px-4 py-2 rounded-lg text-sm">
            <Plus className="w-4 h-4" /> Nouvel événement
          </button>
          <button data-testid="event-seed-btn" onClick={seed}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-amber-300 border border-amber-700 px-4 py-2 rounded-lg text-sm">
            <Sparkles className="w-4 h-4" /> Événements initiaux
          </button>
          <button data-testid="event-reminders-btn" onClick={runReminders}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-cyan-300 border border-cyan-700 px-4 py-2 rounded-lg text-sm">
            <Bell className="w-4 h-4" /> Rappels date limite
          </button>
          <button data-testid="event-digest-btn" onClick={runDigest}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-violet-300 border border-violet-700 px-4 py-2 rounded-lg text-sm">
            <Bell className="w-4 h-4" /> Relances (courriel)
          </button>
        </>} />

      {/* Onglets */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="event-tabs">
        {[
          { k: "upcoming", l: "Événements à venir", icon: CalendarDays },
          { k: "teaching", l: "J'enseigne", icon: GraduationCap },
          { k: "participants", l: "Inscriptions dojo", icon: Users },
          { k: "announcements", l: "🚨 Annonces auto", icon: Bell },
          { k: "defis", l: "🔥 Défis Kaizen", icon: Flame },
          { k: "archives", l: "Archives", icon: Clock },
        ].map((t) => (
          <button key={t.k} onClick={() => { setTab(t.k); setDetailId(null); }} data-testid={`tab-${t.k}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition-colors ${
              tab === t.k ? "bg-amber-900/40 border-amber-600 text-amber-200" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600"
            }`}>
            <t.icon className="w-4 h-4" /> {t.l}
          </button>
        ))}
      </div>

      {tab === "participants" ? (
        <ParticipantsOverview events={events} onOpen={(id) => { setTab("upcoming"); setDetailId(id); }} />
      ) : tab === "announcements" ? (
        <AnnouncementsPanel />
      ) : tab === "defis" ? (
        <DefisKaizenPanel />
      ) : detailId ? (
        <EventDetail id={detailId} onClose={() => setDetailId(null)} onEdit={(ev) => setEditModal({ form: ev, id: ev.id })}
          onDelete={del} onCancel={cancelEvent} />
      ) : (
        <>
          {/* Filtres */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="filter-type"
              className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-zinc-300">
              <option value="">Tous les types</option>
              {EVENT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <select value={filterDisc} onChange={(e) => setFilterDisc(e.target.value)} data-testid="filter-discipline"
              className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-zinc-300">
              <option value="">Toutes les disciplines</option>
              {disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />
          ) : filtered.length === 0 ? (
            <div className="text-center text-zinc-500 py-20" data-testid="events-empty">
              {tab === "teaching" ? "Aucun événement où tu enseignes. Coche « J'enseigne à cet événement » sur une fiche."
                : tab === "archives" ? "Aucun événement archivé ou passé."
                : "Aucun événement. Clique « Événements initiaux » ou « Nouvel événement »."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="events-grid">
              {filtered.map((e) => (
                <EventCard key={e.id} e={e} onOpen={() => setDetailId(e.id)}
                  onDuplicate={() => duplicate(e.id)} onDelete={() => del(e.id)}
                  onCancel={() => cancelEvent(e.id, e.event_title)} />
              ))}
            </div>
          )}
        </>
      )}

      {editModal && (
        <EventEditModal initial={editModal.form} eventId={editModal.id}
          onClose={() => setEditModal(null)} onSaved={() => { setEditModal(null); load(); }} />
      )}
    </div>
  );
}

function EventCard({ e, onOpen, onDuplicate, onDelete, onCancel }) {
  const st = STATUS_BADGE[e.status] || STATUS_BADGE.published;
  const isCancelled = e.status === "cancelled";
  return (
    <div data-testid={`event-card-${e.id}`}
      className={`rounded-xl border overflow-hidden transition-colors flex flex-col ${isCancelled ? "border-red-900/50 bg-red-950/10 opacity-80" : "border-zinc-800 bg-zinc-900/60 hover:border-amber-600/50"}`}>
      <div className="h-28 bg-gradient-to-br from-zinc-800 to-zinc-950 relative flex items-center justify-center">
        {e.poster_image
          ? <img src={mediaUrl(e.poster_image)} alt="" className="w-full h-full object-cover" />
          : <CalendarDays className="w-10 h-10 text-zinc-700" />}
        {e.featured_event && (
          <span className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold bg-amber-500 text-black px-2 py-0.5 rounded-full">
            <Star className="w-3 h-3" /> Vedette
          </span>)}
        <span className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.c}`}>{st.l}</span>
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">{TYPE_LABEL(e.event_type)}</span>
          {e.discipline && <span className="text-[10px] text-zinc-500">{e.discipline}</span>}
          {e.is_full && <span className="text-[10px] font-bold text-red-300 bg-red-500/10 px-2 py-0.5 rounded">COMPLET</span>}
          {e.instructor_teaching && <span className="text-[10px] font-bold text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded flex items-center gap-1"><GraduationCap className="w-3 h-3" /> J'enseigne</span>}
        </div>
        <button onClick={onOpen} className="text-left font-bold text-lg leading-tight hover:text-amber-300 transition-colors" data-testid={`event-open-${e.id}`}>
          {e.event_title}
        </button>
        <div className="text-xs text-zinc-400 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {fmtRange(e.start_date, e.end_date)}</div>
        {e.city && <div className="text-xs text-zinc-500 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {e.city}</div>}
        {e.short_description && <p className="text-xs text-zinc-500 line-clamp-2">{e.short_description}</p>}
        <div className="mt-auto flex items-center justify-between pt-2">
          <span className="text-xs text-zinc-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {e.participant_count || 0} inscrit(s) dojo</span>
          <div className="flex items-center gap-1">
            {!isCancelled && (
              <button onClick={onCancel} title="Annuler l'événement + stopper les annonces auto" data-testid={`event-cancel-${e.id}`}
                className="p-1.5 rounded hover:bg-zinc-800 text-orange-400"><Bell className="w-4 h-4" /></button>
            )}
            <button onClick={onDuplicate} title="Dupliquer" className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400"><Copy className="w-4 h-4" /></button>
            <button onClick={onDelete} title="Supprimer" className="p-1.5 rounded hover:bg-zinc-800 text-red-400"><Trash2 className="w-4 h-4" /></button>
            <button onClick={onOpen} className="p-1.5 rounded hover:bg-zinc-800 text-amber-400"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventDetail({ id, onClose, onEdit, onDelete, onCancel }) {
  const [event, setEvent] = useState(null);
  const [parts, setParts] = useState([]);
  const [stats, setStats] = useState({});
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ev, pr] = await Promise.all([
        api.get(`/dojo-events/${id}`),
        api.get(`/dojo-events/${id}/participations`),
      ]);
      setEvent(ev.data.event); setParts(pr.data.participations || []); setStats(pr.data.stats || {});
    } catch { toast.error("Erreur chargement"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patchPart = async (pid, upd) => {
    try { await api.patch(`/dojo-events/participations/${pid}`, upd); load(); }
    catch { toast.error("Erreur"); }
  };
  const delPart = async (pid) => {
    if (!window.confirm("Retirer ce participant ?")) return;
    try { await api.delete(`/dojo-events/participations/${pid}`); load(); } catch { toast.error("Erreur"); }
  };

  const [regLinkOpen, setRegLinkOpen] = useState(null); // null | { preselected: [] }
  const sendRegLink = () => setRegLinkOpen({ preselected: [] });
  const downloadBlob = async (url, filename, newTab = false) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      if (newTab) { window.open(href, "_blank"); return; }
      const a = document.createElement("a");
      a.href = href; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
    } catch { toast.error("Erreur téléchargement"); }
  };

  const roomShare = parts.filter((p) => p.wants_shared_room && p.status !== "annule");
  const carpool = parts.filter((p) => p.wants_carpool && p.status !== "annule");

  const isBBQ = ((event?.tags || []).includes("potluck")) || /bbq/i.test(event?.event_title || "");
  const bbqActive = parts.filter((p) => p.status !== "annule");
  const bbqPeople = bbqActive.reduce((s, p) => s + (Number(p.party_size) || 1), 0);
  const bbqDishes = bbqActive.filter((p) => (p.potluck_dish || "").trim());

  const emailBilan = async () => {
    try {
      await api.post(`/dojo-events/${event.id}/bilan/email`);
      toast.success("Bilan envoyé par courriel au Kru 📧");
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible (Gmail requis)"); }
  };

  const [uploading, setUploading] = useState(false);
  const uploadPhotos = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/dojo-events/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.count} photo(s) ajoutée(s)`);
      load();
    } catch { toast.error("Erreur upload photos"); }
    setUploading(false);
  };
  const delPhoto = async (photoId) => {
    try { await api.delete(`/dojo-events/${id}/photos/${photoId}`); load(); } catch { toast.error("Erreur"); }
  };
  const savePhotoCaption = async (photoId, caption) => {
    try { await api.patch(`/dojo-events/${id}/photos/${photoId}`, { caption }); } catch { /* silencieux */ }
  };
  const setCover = async (photoId) => {
    try { await api.post(`/dojo-events/${id}/photos/${photoId}/set-cover`); toast.success("Photo définie comme couverture"); load(); }
    catch { toast.error("Erreur"); }
  };
  const [dragIdx, setDragIdx] = useState(null);
  const onDrop = async (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); return; }
    const photos = [...(event.photos || [])];
    const [moved] = photos.splice(dragIdx, 1);
    photos.splice(dropIdx, 0, moved);
    setEvent({ ...event, photos });
    setDragIdx(null);
    try { await api.put(`/dojo-events/${id}/photos/reorder`, { order: photos.map((p) => p.id) }); }
    catch { toast.error("Erreur réordonnancement"); load(); }
  };
  const [albumPreview, setAlbumPreview] = useState(null);
  const openAlbumPreview = async () => {
    try {
      const res = await api.get(`/dojo-events/${id}/album`, { responseType: "blob" });
      setAlbumPreview(URL.createObjectURL(res.data));
    } catch { toast.error("Erreur album"); }
  };

  if (!event) return <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />;

  return (
    <div data-testid="event-detail" className="space-y-5">
      <button onClick={onClose} className="text-sm text-zinc-400 hover:text-amber-300 flex items-center gap-1">← Retour aux événements</button>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
        {event.poster_image && <img src={mediaUrl(event.poster_image)} alt="" className="w-full h-40 object-cover" />}
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-[10px] uppercase tracking-wide bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">{TYPE_LABEL(event.event_type)}</span>
                {event.instructor_teaching && <span className="text-[10px] font-bold text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded">J'enseigne · {ROLE_LABEL(event.instructor_role)}</span>}
              </div>
              <h2 className="text-2xl font-bold">{event.event_title}</h2>
              <div className="text-sm text-zinc-400 mt-1 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {fmtRange(event.start_date, event.end_date)}</span>
                {event.city && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {event.venue_name || event.city}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => downloadBlob(`/dojo-events/${event.id}/poster`, `affiche_${event.id}.png`, true)} data-testid="event-poster-btn" className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-amber-200 px-3 py-1.5 rounded-lg"><ImageIcon className="w-4 h-4" /> Affiche</button>
              <button onClick={() => onEdit(event)} data-testid="event-edit-btn" className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded-lg"><Pencil className="w-4 h-4" /> Modifier</button>
              {event.status !== "cancelled" && onCancel && (
                <button onClick={() => onCancel(event.id, event.event_title)} data-testid="event-detail-cancel-btn" className="flex items-center gap-1.5 text-sm bg-orange-900/40 hover:bg-orange-800/50 text-orange-200 border border-orange-800 px-3 py-1.5 rounded-lg"><Bell className="w-4 h-4" /> Annuler + stopper annonces</button>
              )}
              <button onClick={() => onDelete(event.id)} className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-red-300 px-3 py-1.5 rounded-lg"><Trash2 className="w-4 h-4" /> Supprimer</button>
            </div>
          </div>

          {event.long_description && <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{event.long_description}</p>}
          {event.public_notes && <div className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">ℹ️ {event.public_notes}</div>}

          <div className="flex items-center gap-2 flex-wrap">
            {event.official_registration_url && <LinkBtn href={event.official_registration_url} icon={ExternalLink} label="Site officiel" testid="link-official" />}
            {event.hotel_url && <LinkBtn href={event.hotel_url} icon={Hotel} label="Réserver l'hôtel" testid="link-hotel" />}
            {event.schedule_url && <LinkBtn href={event.schedule_url} icon={FileText} label="Horaire" testid="link-schedule" />}
            {event.documents_url && <LinkBtn href={event.documents_url} icon={FileText} label="Documents" testid="link-docs" />}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Info label="Date limite dojo" value={fmtDate(event.dojo_deadline)} />
            <Info label="Date limite inscription" value={fmtDate(event.registration_deadline)} />
            <Info label="Date limite hôtel" value={fmtDate(event.hotel_deadline)} />
            {(() => {
              const ci = Number(event.cost_inscription) || 0, ch = Number(event.cost_hotel) || 0, ct = Number(event.cost_transport) || 0;
              const total = ci + ch + ct;
              if (total > 0) return <Info label="Coût / participant" value={`${total.toLocaleString("fr-CA")} $`} />;
              return <Info label="Coût estimé" value={event.estimated_cost || "—"} />;
            })()}
            {event.equipment_required && <Info label="Équipement requis" value={event.equipment_required} />}
            {event.documents_to_bring && <Info label="Documents à apporter" value={event.documents_to_bring} />}
            {event.replacement_instructor && <Info label="Remplaçant au dojo" value={event.replacement_instructor} />}
            {event.organizer_name && <Info label="Organisateur" value={event.organizer_name} />}
          </div>
          {event.internal_notes && <div className="text-xs text-zinc-500 border-t border-zinc-800 pt-3">📝 Notes internes : {event.internal_notes}</div>}
          {ytEmbed(event.highlights_url) && (
            <div data-testid="event-highlights">
              <div className="text-xs uppercase tracking-wide text-red-300 mb-2">Vidéo highlights</div>
              <div className="relative w-full rounded-lg overflow-hidden border border-zinc-800" style={{ paddingBottom: "56.25%" }}>
                <iframe src={ytEmbed(event.highlights_url)} title="Highlights" className="absolute inset-0 w-full h-full"
                  frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </div>
          )}
        </div>
      </div>

      <EventPosterStudio event={event} />

      {/* Bilan / statistiques */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="event-stats-bilan">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-bold flex items-center gap-2"><Activity className="w-5 h-5 text-cyan-400" /> Bilan de l'événement</h3>
          <div className="flex items-center gap-2">
            <button onClick={emailBilan} data-testid="event-bilan-email-btn"
              className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-amber-200 px-3 py-1.5 rounded-lg"><Mail className="w-4 h-4" /> Bilan par courriel</button>
            <button onClick={() => downloadBlob(`/dojo-events/${event.id}/bilan`, `bilan_${event.id}.pdf`, true)} data-testid="event-bilan-pdf-btn"
              className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-cyan-200 px-3 py-1.5 rounded-lg"><FileText className="w-4 h-4" /> Bilan PDF</button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Participants" value={stats.total || 0} tone="text-zinc-100" />
          <MiniStat label="Taux d'inscription" value={`${stats.registration_rate || 0}%`} tone="text-cyan-300" />
          <MiniStat label="Taux de paiement" value={`${stats.payment_rate || 0}%`} tone="text-emerald-300" />
          <MiniStat label="Revenus estimés" value={`${(stats.estimated_revenue || 0).toLocaleString("fr-CA")} $`} tone="text-amber-300" />
        </div>
        {stats.survey_count > 0 && (
          <div className="mt-3 space-y-2" data-testid="event-survey-summary">
            <div className="flex items-center gap-3 bg-amber-950/30 border border-amber-900/40 rounded-lg px-4 py-2.5">
              <span className="text-2xl">⭐</span>
              <div><div className="text-lg font-bold text-amber-300">{stats.survey_avg} / 5</div>
                <div className="text-[11px] text-zinc-400">Satisfaction moyenne · {stats.survey_count} réponse(s)</div></div>
            </div>
            {(stats.survey_comments || []).length > 0 && (
              <div className="space-y-1.5" data-testid="survey-comments">
                {stats.survey_comments.map((c, i) => (
                  <div key={i} className="bg-zinc-900/60 border border-zinc-800 rounded px-3 py-2 text-sm">
                    <span className="text-amber-300">{"⭐".repeat(c.rating || 0)}</span>
                    <span className="text-zinc-300 ml-2">« {c.comment} »</span>
                    <span className="text-[11px] text-zinc-500 ml-1">— {c.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {(stats.unit_cost > 0) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <MiniStat label="Coût inscription" value={`${(stats.cost_inscription || 0).toLocaleString("fr-CA")} $`} tone="text-zinc-300" />
            <MiniStat label="Coût hôtel" value={`${(stats.cost_hotel || 0).toLocaleString("fr-CA")} $`} tone="text-violet-300" />
            <MiniStat label="Coût transport" value={`${(stats.cost_transport || 0).toLocaleString("fr-CA")} $`} tone="text-teal-300" />
            <MiniStat label="Total / participant" value={`${(stats.unit_cost || 0).toLocaleString("fr-CA")} $`} tone="text-amber-300" />
          </div>
        )}
        {!stats.unit_cost && (
          <p className="text-[11px] text-zinc-500 mt-2">💡 Renseigne les coûts détaillés (inscription / hôtel / transport) de l'événement pour calculer le coût par participant et les revenus estimés.</p>
        )}
      </div>

      {regLinkOpen && <SendRegLinkDialog event={event} parts={parts} preselected={regLinkOpen.preselected} onClose={() => setRegLinkOpen(null)} onSent={load} />}
      {/* Tableau de suivi */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-bold flex items-center gap-2"><Users className="w-5 h-5 text-amber-400" /> Suivi des inscriptions dojo</h3>
          <div className="flex items-center gap-2">
            <button onClick={sendRegLink} data-testid="send-reg-link-btn"
              className="flex items-center gap-1.5 text-sm bg-sky-600 hover:bg-sky-500 text-white font-semibold px-3 py-1.5 rounded-lg"><Send className="w-4 h-4" /> Envoyer le lien d'inscription</button>
            <button onClick={() => downloadBlob(`/dojo-events/${event.id}/participations/export`, `participants_${event.id}.csv`)} data-testid="export-csv-btn"
              className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-emerald-300 px-3 py-1.5 rounded-lg"><Download className="w-4 h-4" /> Exporter (CSV)</button>
            <button onClick={() => setAddOpen(true)} data-testid="add-participant-btn" className="flex items-center gap-1.5 text-sm bg-amber-600 hover:bg-amber-500 text-black font-semibold px-3 py-1.5 rounded-lg"><Plus className="w-4 h-4" /> Ajouter un participant</button>
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4" data-testid="participation-stats">
          <MiniStat label="Intéressés" value={stats.interested || 0} />
          <MiniStat label="Inscrits" value={stats.registered || 0} tone="text-cyan-300" />
          <MiniStat label="Payés" value={stats.paid || 0} tone="text-emerald-300" />
          <MiniStat label="Hôtel" value={stats.hotel_booked || 0} tone="text-violet-300" />
          <MiniStat label="Transport" value={stats.transport_needed || 0} tone="text-teal-300" />
          <MiniStat label="Total" value={stats.total || 0} tone="text-amber-300" />
        </div>

        {/* Entonnoir du lien d'inscription : envoyé → cliqué → inscrit */}
        {(() => {
          const sent = parts.filter((p) => p.registration_link_sent_at).length;
          const clicked = parts.filter((p) => p.link_clicked_at).length;
          const registered = parts.filter((p) => p.already_registered || p.status === "inscrit").length;
          const rate = sent ? Math.round((clicked / sent) * 100) : 0;
          if (!sent) return null;
          const clickedNotRegistered = parts.filter((p) => p.link_clicked_at && !(p.already_registered || p.status === "inscrit"));
          return (
            <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="link-funnel-stats">
              <MiniStat label="Liens envoyés" value={sent} tone="text-zinc-300" />
              <MiniStat label="Ont cliqué" value={clicked} tone="text-sky-300" />
              <MiniStat label="Taux de clic" value={`${rate}%`} tone="text-emerald-300" />
              <MiniStat label="Inscrits" value={registered} tone="text-cyan-300" />
            </div>
            {clickedNotRegistered.length > 0 && (
              <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="clicked-not-registered">
                <div className="text-xs font-semibold text-amber-300 mb-2">🔥 Ont cliqué mais pas inscrits ({clickedNotRegistered.length}) — à appeler :</div>
                <div className="flex flex-wrap gap-2">
                  {clickedNotRegistered.map((p) => (
                    <span key={p.id} data-testid={`hot-lead-${p.id}`} className="text-xs bg-zinc-900 border border-zinc-700 rounded-full px-2.5 py-1 text-zinc-200">
                      {p.full_name}{p.phone ? <a href={`tel:${p.phone}`} className="text-emerald-400 ml-1">📞 {p.phone}</a> : p.email ? <span className="text-zinc-500 ml-1">{p.email}</span> : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}
            </>
          );
        })()}

        {isBBQ && (
          <div data-testid="bbq-summary" className="mb-4 rounded-xl border border-blue-500/40 bg-gradient-to-r from-[#0b1e4d] to-[#123a8f]/40 p-4">
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <div className="text-3xl font-black text-white" data-testid="bbq-total-people">{bbqPeople}</div>
                <div className="text-xs uppercase tracking-wide text-blue-200">personnes inscrites 🍔</div>
              </div>
              <div>
                <div className="text-3xl font-black text-white" data-testid="bbq-total-dishes">{bbqDishes.length}</div>
                <div className="text-xs uppercase tracking-wide text-blue-200">plats apportés 🍽️</div>
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="text-[11px] uppercase tracking-wide text-blue-200 mb-1">Plats prévus</div>
                {bbqDishes.length === 0 ? (
                  <p className="text-xs text-blue-100/60">Aucun plat renseigné pour l'instant.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {bbqDishes.map((p) => (
                      <span key={p.id} className="text-[11px] bg-white/10 text-blue-50 rounded-full px-2.5 py-1">
                        {p.potluck_dish} <span className="text-blue-200/70">· {p.full_name}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {parts.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-6">Aucun participant. Ajoute la première personne intéressée.</p>
        ) : (
          <div className="space-y-2" data-testid="participants-list">
            {parts.map((p) => (
              <div key={p.id} data-testid={`participant-${p.id}`} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-950 border border-zinc-800 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{p.full_name} <span className="text-[10px] text-zinc-500 font-normal">· {ROLE_LABEL(p.role)}</span></div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                    {p.discipline && <span>{p.discipline}</span>}
                    {p.email && <span>{p.email}</span>}
                    {p.phone && <span>{p.phone}</span>}
                    {isBBQ && (Number(p.party_size) || 1) > 1 && <span className="text-blue-300">{p.party_size} pers.</span>}
                    {isBBQ && (p.potluck_dish || "").trim() && <span className="text-amber-300">🍽️ {p.potluck_dish}</span>}
                    {p.wants_carpool && <span className="text-teal-400">covoiturage</span>}
                    {p.wants_shared_room && <span className="text-violet-400">chambre partagée</span>}
                    {p.link_clicked_at ? (
                      <span className="text-emerald-400 flex items-center gap-0.5" data-testid={`clicked-${p.id}`} title={`Lien cliqué le ${(p.link_clicked_at || "").slice(0, 10)}${p.link_click_count > 1 ? ` (${p.link_click_count}×)` : ""}`}>🔗 Lien cliqué</span>
                    ) : p.registration_link_sent_at ? (
                      <span className="text-zinc-500 flex items-center gap-0.5" data-testid={`sent-${p.id}`} title={`Lien envoyé le ${(p.registration_link_sent_at || "").slice(0, 10)}`}>✉️ Lien envoyé · pas encore cliqué</span>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Toggle on={p.already_registered} label="Inscrit" onClick={() => patchPart(p.id, { already_registered: !p.already_registered })} />
                  <Toggle on={p.already_paid} label="Payé" onClick={() => patchPart(p.id, { already_paid: !p.already_paid })} />
                  <Toggle on={p.hotel_booked} label="Hôtel" onClick={() => patchPart(p.id, { hotel_booked: !p.hotel_booked })} />
                  <select value={p.status} onChange={(e) => patchPart(p.id, { status: e.target.value })} data-testid={`status-${p.id}`}
                    className="text-[11px] bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-300">
                    {Object.entries(PART_STATUS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                  </select>
                  <button onClick={() => setRegLinkOpen({ preselected: [p.id] })} className="p-1 text-zinc-500 hover:text-sky-300" title="Envoyer le lien d'inscription à cette personne" data-testid={`send-link-one-${p.id}`}><Send className="w-4 h-4" /></button>
                  <button onClick={() => delPart(p.id)} className="p-1 text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chambres & Covoiturage */}
      {(roomShare.length > 0 || carpool.length > 0) && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="rooms-carpool">
          <h3 className="font-bold flex items-center gap-2 mb-3"><Hotel className="w-5 h-5 text-violet-400" /> Chambres & covoiturage</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-violet-300 mb-2">Chambre partagée ({roomShare.length})</div>
              {roomShare.length === 0 ? <p className="text-xs text-zinc-500">Personne pour l'instant.</p> : (
                <div className="space-y-1.5">
                  {roomShare.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 text-sm bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2">
                      <span className="font-medium">{p.full_name}</span>
                      {p.phone && <span className="text-[11px] text-zinc-500">{p.phone}</span>}
                      {p.email && <span className="text-[11px] text-zinc-500">{p.email}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-teal-300 mb-2">Covoiturage ({carpool.length})</div>
              {carpool.length === 0 ? <p className="text-xs text-zinc-500">Personne pour l'instant.</p> : (
                <div className="space-y-1.5">
                  {carpool.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 text-sm bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2">
                      <span className="font-medium">{p.full_name}</span>
                      {p.city_area && <span className="text-[11px] text-zinc-500">{p.city_area}</span>}
                      {p.phone && <span className="text-[11px] text-zinc-500">{p.phone}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Galerie photos */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="event-photos">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-bold flex items-center gap-2"><ImageIcon className="w-5 h-5 text-amber-400" /> Galerie photos</h3>
          <div className="flex items-center gap-2">
            {(event.photos && event.photos.length > 0) && (
              <button onClick={openAlbumPreview} data-testid="album-btn"
                className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-amber-200 px-3 py-1.5 rounded-lg"><ImageIcon className="w-4 h-4" /> Album souvenir</button>
            )}
            <label className="flex items-center gap-1.5 text-sm bg-amber-600 hover:bg-amber-500 text-black font-semibold px-3 py-1.5 rounded-lg cursor-pointer" data-testid="photo-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter des photos
              <input type="file" accept="image/*" multiple className="hidden" data-testid="photo-input"
                onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }} />
            </label>
          </div>
        </div>
        {(!event.photos || event.photos.length === 0) ? (
          <p className="text-sm text-zinc-500 text-center py-6">Aucune photo. Ajoute des souvenirs de l'événement (visibles publiquement sur la fiche).</p>
        ) : (
          <>
          <p className="text-[11px] text-zinc-500 mb-2">Glisse-dépose pour réordonner · « Couverture » pour choisir l'affiche.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photos-grid">
            {event.photos.map((ph, idx) => {
              const isCover = event.poster_image && event.poster_image === ph.url;
              return (
              <div key={ph.id} draggable onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
                data-testid={`photo-tile-${ph.id}`}
                className={`rounded-lg overflow-hidden border bg-zinc-950 cursor-move ${isCover ? "border-amber-500" : "border-zinc-800"}`}>
                <div className="relative group aspect-square">
                  <img src={mediaUrl(ph.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  {isCover && <span className="absolute top-1 left-1 bg-amber-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5"><Star className="w-2.5 h-2.5" /> Couverture</span>}
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!isCover && <button onClick={() => setCover(ph.id)} data-testid={`photo-cover-${ph.id}`} title="Définir comme couverture" className="bg-black/70 hover:bg-amber-500 hover:text-black text-white rounded-full p-1"><Star className="w-3.5 h-3.5" /></button>}
                    <button onClick={() => delPhoto(ph.id)} data-testid={`photo-del-${ph.id}`} className="bg-black/70 hover:bg-red-600 text-white rounded-full p-1"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <input defaultValue={ph.caption || ""} placeholder="Légende…" data-testid={`photo-caption-${ph.id}`}
                  onBlur={(e) => savePhotoCaption(ph.id, e.target.value)}
                  className="w-full bg-transparent border-t border-zinc-800 px-2 py-1.5 text-xs text-zinc-300 placeholder-zinc-600 focus:outline-none focus:bg-zinc-900" />
              </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {albumPreview && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(albumPreview); setAlbumPreview(null); }} data-testid="album-preview-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 max-w-md w-full">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold flex items-center gap-2"><ImageIcon className="w-5 h-5 text-amber-400" /> Aperçu de l'album</h3>
              <button onClick={() => { URL.revokeObjectURL(albumPreview); setAlbumPreview(null); }} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <img src={albumPreview} alt="Album" className="w-full rounded-lg border border-zinc-800 max-h-[60vh] object-contain" data-testid="album-preview-img" />
            <a href={albumPreview} download={`album_${event.id}.png`} data-testid="album-download-btn"
              className="mt-3 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 text-black font-bold py-2.5 rounded-lg"><Download className="w-4 h-4" /> Télécharger l'album</a>
          </div>
        </div>
      )}


      {addOpen && (
        <ParticipantModal onClose={() => setAddOpen(false)} onSaved={async (form) => {
          try { await api.post(`/dojo-events/${id}/participations`, form); toast.success("Participant ajouté"); setAddOpen(false); load(); }
          catch { toast.error("Erreur"); }
        }} />
      )}
    </div>
  );
}

function AnnouncementsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/dojo-events/announcements/active"); setItems(data.items || []); }
    catch { toast.error("Erreur chargement annonces"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancelItem = async (it) => {
    if (!window.confirm(`Annuler / mettre en pause cette annonce automatique ?\n\n« ${it.title} »`)) return;
    setBusy(it.source + it.id);
    try {
      await api.post("/dojo-events/announcements/cancel-item", { source: it.source, item_id: it.id });
      toast.success("Annonce annulée / mise en pause ✓");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(null);
  };

  const sendErratum = async (it) => {
    setBusy("erratum" + it.id);
    try {
      const { data } = await api.post("/dojo-events/announcements/erratum", { broadcast_id: it.id, dry_run: true });
      if (!data.recipients) { toast.info("Aucun destinataire trouvé pour cette diffusion."); setBusy(null); return; }
      if (!window.confirm(`📧 Envoyer le courriel d'ERRATUM (« système error : activité annulée ») à ${data.recipients} membre(s) ayant reçu cette invitation ?\n\nSujet : ${data.subject}`)) { setBusy(null); return; }
      const res = await api.post("/dojo-events/announcements/erratum", { broadcast_id: it.id, dry_run: false });
      toast.success(`✅ Erratum envoyé à ${res.data.sent}/${res.data.total} membre(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi erratum"); }
    setBusy(null);
  };

  const KIND_TONE = {
    "Proposition d'événement (agent IA)": "text-cyan-300 border-cyan-800",
    "Campagne automatique proposée": "text-amber-300 border-amber-800",
    "Publication réseaux programmée": "text-violet-300 border-violet-800",
    "Diffusion courriel membre": "text-emerald-300 border-emerald-800",
  };

  return (
    <div className="space-y-4" data-testid="announcements-panel">
      <div className="rounded-xl border border-orange-800/50 bg-orange-950/10 p-4">
        <h3 className="font-bold text-orange-200 flex items-center gap-2"><Bell className="w-5 h-5" /> Annonces & campagnes automatiques</h3>
        <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
          Ici tu vois <b>tout ce que le système peut envoyer automatiquement</b> (propositions d'événements de l'agent IA,
          campagnes marketing suggérées, publications programmées, diffusions courriel).
          Utilise <b>« Annuler »</b> pour stopper une annonce, ou <b>« Envoyer un erratum »</b> pour prévenir
          les membres qu'une activité est annulée.
        </p>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />
      ) : items.length === 0 ? (
        <div className="text-center text-zinc-500 py-16" data-testid="announcements-empty">✅ Aucune annonce automatique en cours.</div>
      ) : (
        <div className="space-y-2" data-testid="announcements-list">
          {items.map((it) => (
            <div key={`${it.source}-${it.id}`} data-testid={`announcement-${it.id}`}
              className="flex items-center gap-3 p-3.5 rounded-xl bg-zinc-900/60 border border-zinc-800 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[10px] uppercase tracking-wide bg-zinc-950 border px-2 py-0.5 rounded ${KIND_TONE[it.kind] || "text-zinc-400 border-zinc-700"}`}>{it.kind}</span>
                  <span className="text-[10px] text-zinc-500">{it.status}</span>
                  {it.date && <span className="text-[10px] text-zinc-600">· {it.date}</span>}
                </div>
                <div className="font-semibold text-sm truncate text-zinc-100">{it.title}</div>
                {it.subtitle && <div className="text-[11px] text-zinc-500 truncate">{it.subtitle}</div>}
              </div>
              <div className="flex items-center gap-2">
                {it.can_erratum && (
                  <button onClick={() => sendErratum(it)} disabled={busy === "erratum" + it.id} data-testid={`erratum-${it.id}`}
                    className="flex items-center gap-1.5 text-xs bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50">
                    {busy === "erratum" + it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Envoyer un erratum
                  </button>
                )}
                {it.can_cancel && (
                  <button onClick={() => cancelItem(it)} disabled={busy === it.source + it.id} data-testid={`cancel-announcement-${it.id}`}
                    className="flex items-center gap-1.5 text-xs bg-red-900/50 hover:bg-red-800/60 text-red-200 border border-red-800 px-3 py-1.5 rounded-lg disabled:opacity-50">
                    {busy === it.source + it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Annuler
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ParticipantsOverview({ events, onOpen }) {
  const active = events.filter((e) => e.status !== "archived" && !isPast(e));
  return (
    <div className="space-y-3" data-testid="participants-overview">
      {active.length === 0 ? (
        <p className="text-sm text-zinc-500 text-center py-16">Aucun événement actif. Crée un événement pour suivre les inscriptions.</p>
      ) : active.map((e) => (
        <button key={e.id} onClick={() => onOpen(e.id)} data-testid={`overview-${e.id}`}
          className="w-full text-left flex items-center gap-4 p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 hover:border-amber-600 transition-colors">
          <CalendarDays className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold truncate">{e.event_title}</div>
            <div className="text-xs text-zinc-500">{fmtRange(e.start_date, e.end_date)} · {e.city || "—"}</div>
          </div>
          <span className="text-sm text-zinc-400 flex items-center gap-1.5"><Users className="w-4 h-4" /> {e.participant_count || 0}</span>
          <ChevronRight className="w-4 h-4 text-zinc-600" />
        </button>
      ))}
    </div>
  );
}

function EventEditModal({ initial, eventId, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY_EVENT, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.event_title.trim()) { toast.error("Titre requis"); return; }
    setBusy(true);
    try {
      const payload = { ...form, tags: Array.isArray(form.tags) ? form.tags : String(form.tags || "").split(",").map((t) => t.trim()).filter(Boolean) };
      if (eventId) await api.put(`/dojo-events/${eventId}`, payload);
      else await api.post("/dojo-events", payload);
      toast.success(eventId ? "Événement mis à jour" : "Événement créé");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-zinc-900 border-zinc-700" data-testid="event-edit-modal">
        <DialogHeader><DialogTitle>{eventId ? "Modifier l'événement" : "Nouvel événement"}</DialogTitle>
          <DialogDescription>Renseigne les détails de l'événement (dates, lieu, liens, date limite).</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field className="md:col-span-2" label="Titre de l'événement" value={form.event_title} onChange={(v) => set("event_title", v)} testid="f-title" />
          <Sel label="Type" value={form.event_type} onChange={(v) => set("event_type", v)} options={EVENT_TYPES.map((t) => ({ v: t.v, l: t.l }))} testid="f-type" />
          <Field label="Discipline" value={form.discipline} onChange={(v) => set("discipline", v)} placeholder="Kickboxing / Muay Thaï…" testid="f-disc" />
          <Field label="Date de début" type="date" value={form.start_date} onChange={(v) => set("start_date", v)} testid="f-start" />
          <Field label="Date de fin" type="date" value={form.end_date} onChange={(v) => set("end_date", v)} testid="f-end" />
          <Field label="Ville" value={form.city} onChange={(v) => set("city", v)} testid="f-city" />
          <Field label="Lieu (venue)" value={form.venue_name} onChange={(v) => set("venue_name", v)} testid="f-venue" />
          <Field className="md:col-span-2" label="Adresse du lieu" value={form.venue_address} onChange={(v) => set("venue_address", v)} testid="f-address" />
          <Field className="md:col-span-2" label="Description courte" value={form.short_description} onChange={(v) => set("short_description", v)} testid="f-short" />
          <Area className="md:col-span-2" label="Description longue" value={form.long_description} onChange={(v) => set("long_description", v)} testid="f-long" />
          <Field label="Organisateur" value={form.organizer_name} onChange={(v) => set("organizer_name", v)} testid="f-org" />
          <Field label="Contact organisateur" value={form.organizer_contact} onChange={(v) => set("organizer_contact", v)} testid="f-orgc" />
          <Field label="Lien inscription officielle" value={form.official_registration_url} onChange={(v) => set("official_registration_url", v)} testid="f-regurl" />
          <Field label="Lien hôtel" value={form.hotel_url} onChange={(v) => set("hotel_url", v)} testid="f-hotelurl" />
          <Field label="Lien horaire" value={form.schedule_url} onChange={(v) => set("schedule_url", v)} testid="f-schedurl" />
          <Field label="Lien documents" value={form.documents_url} onChange={(v) => set("documents_url", v)} testid="f-docsurl" />
          <Field className="md:col-span-2" label="Vidéo YouTube highlights (URL)" value={form.highlights_url} onChange={(v) => set("highlights_url", v)} placeholder="https://youtu.be/…" testid="f-highlights" />
          <Field className="md:col-span-2" label="URL affiche (image)" value={form.poster_image} onChange={(v) => set("poster_image", v)} testid="f-poster" />
          <Sel label="Mon rôle" value={form.instructor_role} onChange={(v) => set("instructor_role", v)} options={ROLES.map((r) => ({ v: r.v, l: r.l }))} testid="f-role" />
          <Field label="Coût — Inscription ($)" type="number" value={form.cost_inscription} onChange={(v) => set("cost_inscription", parseFloat(v) || 0)} placeholder="0" testid="f-cost-inscription" />
          <Field label="Coût — Hôtel ($)" type="number" value={form.cost_hotel} onChange={(v) => set("cost_hotel", parseFloat(v) || 0)} placeholder="0" testid="f-cost-hotel" />
          <Field label="Coût — Transport ($)" type="number" value={form.cost_transport} onChange={(v) => set("cost_transport", parseFloat(v) || 0)} placeholder="0" testid="f-cost-transport" />
          <Field label="Coût (note libre, optionnel)" value={form.estimated_cost} onChange={(v) => set("estimated_cost", v)} placeholder="ex: variable selon catégorie" testid="f-cost" />
          <Field label="Date limite dojo" type="date" value={form.dojo_deadline} onChange={(v) => set("dojo_deadline", v)} testid="f-dojodl" />
          <Field label="Date limite inscription" type="date" value={form.registration_deadline} onChange={(v) => set("registration_deadline", v)} testid="f-regdl" />
          <Field label="Date limite hôtel" type="date" value={form.hotel_deadline} onChange={(v) => set("hotel_deadline", v)} testid="f-hoteldl" />
          <Field label="Remplaçant au dojo" value={form.replacement_instructor} onChange={(v) => set("replacement_instructor", v)} testid="f-repl" />
          <Field label="Équipement requis" value={form.equipment_required} onChange={(v) => set("equipment_required", v)} testid="f-equip" />
          <Field label="Documents à apporter" value={form.documents_to_bring} onChange={(v) => set("documents_to_bring", v)} testid="f-docbring" />
          <Field className="md:col-span-2" label="Note publique (encadré info)" value={form.public_notes} onChange={(v) => set("public_notes", v)} testid="f-pubnote" />
          <Area className="md:col-span-2" label="Notes internes (privées)" value={form.internal_notes} onChange={(v) => set("internal_notes", v)} testid="f-intnote" />
          <Sel label="Statut" value={form.status} onChange={(v) => set("status", v)} options={[
            { v: "draft", l: "Brouillon" }, { v: "published", l: "Publié" }, { v: "closed", l: "Fermé" }, { v: "archived", l: "Archivé" }, { v: "cancelled", l: "Annulé" }]} testid="f-status" />
          <Field label="Tags (séparés par ,)" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(v) => set("tags", v)} testid="f-tags" />
          <div className="md:col-span-2 flex items-center gap-6 flex-wrap pt-1">
            <Check label="J'enseigne à cet événement (Muay Thaï, etc.)" checked={form.instructor_teaching} onChange={(v) => set("instructor_teaching", v)} testid="f-teaching" />
            <Check label="Événement vedette" checked={form.featured_event} onChange={(v) => set("featured_event", v)} testid="f-featured" />
            <Check label="Complet (plus de places)" checked={form.is_full} onChange={(v) => set("is_full", v)} testid="f-full" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">Annuler</button>
          <button onClick={submit} disabled={busy} data-testid="event-save-btn" className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-black font-bold text-sm disabled:opacity-50">{busy ? "…" : "Enregistrer"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParticipantModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", role: "member", student_name: "", discipline: "", age_group: "",
    wants_to_attend: true, already_registered: false, already_paid: false, hotel_booked: false,
    transportation_needed: false, wants_shared_room: false, wants_carpool: false, notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-zinc-900 border-zinc-700" data-testid="participant-modal">
        <DialogHeader><DialogTitle>Ajouter un participant (dojo)</DialogTitle>
          <DialogDescription>Enregistre une personne intéressée à participer avec le dojo.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field className="md:col-span-2" label="Nom complet" value={form.full_name} onChange={(v) => set("full_name", v)} testid="p-name" />
          <Field label="Courriel" value={form.email} onChange={(v) => set("email", v)} testid="p-email" />
          <Field label="Téléphone" value={form.phone} onChange={(v) => set("phone", v)} testid="p-phone" />
          <Sel label="Rôle" value={form.role} onChange={(v) => set("role", v)} options={[
            { v: "member", l: "Membre" }, { v: "parent", l: "Parent" }, { v: "professor", l: "Professeur" },
            { v: "coach", l: "Coach" }, { v: "instructor", l: "Instructeur" }, { v: "admin", l: "Admin" }]} testid="p-role" />
          <Field label="Discipline" value={form.discipline} onChange={(v) => set("discipline", v)} testid="p-disc" />
          <Field label="Nom de l'élève (si parent)" value={form.student_name} onChange={(v) => set("student_name", v)} testid="p-student" />
          <Field label="Groupe d'âge" value={form.age_group} onChange={(v) => set("age_group", v)} testid="p-age" />
          <Area className="md:col-span-2" label="Notes" value={form.notes} onChange={(v) => set("notes", v)} testid="p-notes" />
          <div className="md:col-span-2 grid grid-cols-2 gap-2">
            <Check label="Déjà inscrit (site officiel)" checked={form.already_registered} onChange={(v) => set("already_registered", v)} testid="p-registered" />
            <Check label="Déjà payé" checked={form.already_paid} onChange={(v) => set("already_paid", v)} testid="p-paid" />
            <Check label="Hôtel réservé" checked={form.hotel_booked} onChange={(v) => set("hotel_booked", v)} testid="p-hotel" />
            <Check label="Transport à prévoir" checked={form.transportation_needed} onChange={(v) => set("transportation_needed", v)} testid="p-transport" />
            <Check label="Chambre partagée" checked={form.wants_shared_room} onChange={(v) => set("wants_shared_room", v)} testid="p-shared" />
            <Check label="Covoiturage" checked={form.wants_carpool} onChange={(v) => set("wants_carpool", v)} testid="p-carpool" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">Annuler</button>
          <button onClick={() => { if (!form.full_name.trim()) { toast.error("Nom requis"); return; } onSaved(form); }} data-testid="participant-save-btn"
            className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-black font-bold text-sm">Ajouter</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Petits composants ────────────────────────────────────────────────────────
const LinkBtn = ({ href, icon: Icon, label, testid }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
    className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-1.5 rounded-lg">
    <Icon className="w-4 h-4" /> {label}
  </a>
);
const Info = ({ label, value }) => (
  <div className="rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2">
    <div className="text-[9px] uppercase tracking-wide text-zinc-500">{label}</div>
    <div className="text-zinc-200 mt-0.5">{value}</div>
  </div>
);
const MiniStat = ({ label, value, tone }) => (
  <div className="rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2 text-center">
    <div className={`text-xl font-bold ${tone || "text-zinc-100"}`}>{value}</div>
    <div className="text-[10px] text-zinc-500">{label}</div>
  </div>
);
const Toggle = ({ on, label, onClick }) => (
  <button onClick={onClick} className={`text-[11px] px-2 py-1 rounded border transition-colors ${on ? "bg-emerald-600/20 border-emerald-600 text-emerald-300" : "bg-zinc-900 border-zinc-700 text-zinc-500 hover:border-zinc-500"}`}>{label}</button>
);
const Field = ({ label, value, onChange, type = "text", placeholder, testid, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs text-zinc-400">{label}</span>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
      className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 text-sm text-zinc-100" />
  </label>
);
const Area = ({ label, value, onChange, testid, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs text-zinc-400">{label}</span>
    <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 text-sm text-zinc-100" />
  </label>
);
const Sel = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-xs text-zinc-400">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 text-sm text-zinc-100">
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </label>
);
const Check = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="accent-amber-500 w-4 h-4" />
    {label}
  </label>
);


// 🎨 Studio d'affiche IA + QR (formation en ligne) + partage Facebook assisté
const FORMATION_URL = `${PUBLIC_ORIGIN}/kaizen-en-ligne`;

function EventPosterStudio({ event }) {
  const initial = useMemo(() => ({
    event_title: event.event_title || "",
    event_dates: fmtRange(event.start_date, event.end_date),
    event_location: event.venue_name || event.city || "",
    subtitle: event.short_description || event.long_description?.slice(0, 120) || "",
    teaching: !!event.instructor_teaching,
    qr_dest: "evenements",
  }), [event]);
  return (
    <div data-testid="event-poster-studio">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-rose-400" />
        <h3 className="font-bold">Affiche IA & code QR</h3>
      </div>
      <p className="text-xs text-zinc-400 mb-3">Même studio que le <Link to="/app/generateur-affiches" className="underline text-rose-300">Générateur d'affiches</Link> : donne ton idée, l'IA compose l'affiche avec tes photos ou une image générée, puis partage-la sur Facebook.</p>
      <PosterStudio initial={initial} eventPhotos={event.photos || []} showIdea />
    </div>
  );
}
